import React, { useState } from "react";
import { Button, Form, Modal, ModalTitle } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ButtonLoading } from "go-form";
import useFlash from "go-alert/AlertMessage";
import handleError from "go-app/services/errors";
import { FormDirty } from "go-form/components/FormDirty";
import { organizationApi } from "../../../../services/Api/Organization/organizationApi";
import { OrganizationMeApi } from "../../../../services/Api/types";
import { api } from "../../../../services/Security/api";
import { selectOrganization } from "../../../../services/organizations/selectors";
import InvoiceDetailsFormContent from "./InvoiceDetailsFormContent";

interface Props {
	details: OrganizationMeApi;
	isModalForm: boolean;
	isModalShown?: boolean;
	onHideModal?: () => void;
	onFetchOrganizationMeDetails?: (data: OrganizationMeApi) => void;
}

const InvoiceDetailsForm = ({
	details,
	isModalForm,
	isModalShown,
	onHideModal,
	onFetchOrganizationMeDetails,
}: Props) => {
	const form = useForm<OrganizationMeApi>({
		criteriaMode: "all",
		defaultValues: {
			tax_id_no: details.tax_id_no,
			company_name: details.company_name,
			address: {
				country: details.address?.country,
				zip_code: details.address?.zip_code,
				city: details.address?.city,
				street: details.address?.street,
				build_nr: details.address?.build_nr,
				flat_nr: details.address?.flat_nr,
			},
			contact: {
				email: details.contact?.email,
				phone_number: details.contact?.phone_number,
			},
		},
		shouldUnregister: false,
	});
	const { handleSubmit, setError, formState, reset, clearErrors } = form;
	const { t } = useTranslation();
	const [loading, setLoading] = useState<boolean>(false);
	const { addFlash, addSuccessFlash } = useFlash();
	const organization = useSelector(selectOrganization);

	const onSubmit = handleSubmit(async (data) => {
		setLoading(true);
		try {
			if (organization && organization.id) {
				await api.user().updateOrganizationMePatch(organization.id, data);
				const res = await organizationApi.getOrganizationMe(organization.id);
				addSuccessFlash(t("common.flash.saved", { ns: "lib" }));
				reset(data);
				clearErrors();
				if (isModalForm) {
					onFetchOrganizationMeDetails && onFetchOrganizationMeDetails(res);
					onHideModal && onHideModal();
				}
			}
		} catch (err) {
			handleError.form(err, setError, addFlash);
			setLoading(false);
		}
		setLoading(false);
	});

	return isModalForm ? (
		<>
			<Modal show={isModalShown} onHide={onHideModal} size="lg">
				<FormDirty formState={formState}>
					<Modal.Header>
						<ModalTitle>{t("modules.settings.field.invoice_details.title")}</ModalTitle>
					</Modal.Header>
					<Modal.Body>
						<InvoiceDetailsFormContent form={form} />
					</Modal.Body>
					<Modal.Footer>
						<ButtonLoading loading={loading} onClick={onSubmit}>
							{t("common.action.save", { ns: "lib" })}
						</ButtonLoading>
						<Button variant="light" type="button" onClick={() => onHideModal && onHideModal()}>
							{t("common.action.cancel", { ns: "lib" })}
						</Button>
					</Modal.Footer>
				</FormDirty>
			</Modal>
		</>
	) : (
		<FormDirty loading={loading} onSubmit={onSubmit} formState={formState}>
			<InvoiceDetailsFormContent form={form} />
		</FormDirty>
	);
};

export default InvoiceDetailsForm;
